import { useState } from "react";
import {
  addMinutes,
  format,
  formatDistanceToNowStrict,
  isPast,
  parseISO,
} from "date-fns";
import {
  Box,
  Button,
  CardContent,
  Chip,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type { ChipProps } from "@mui/material";
import VideocamIcon from "@mui/icons-material/Videocam";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ScheduleIcon from "@mui/icons-material/Schedule";
import WhiteCard from "src/shared/components/WhiteCard";
import { Encounter, EncounterStatus } from "src/api/types";

/* ---------- Example usage ----------

const meeting: Meeting = {
  id: 4,
  duration_minutes: 15,
  room_url: "https://meet.google.com/zfv-csmt-muo",
  scheduled_at: "2026-10-20T19:45:00+00:00",
  status: "requested",
};

/* ---------- Types matching the BE payload ---------- */

// export interface Meeting {
//   id: number;
//   duration_minutes: number;
//   room_url: string;
//   scheduled_at: string;
//   status: EncounterStatus;
// }

export interface MeetingCardProps {
  meeting: Encounter;
  /** Optional actions; buttons are hidden when not provided */
  onCancel?: (meeting: Encounter) => void;
  onReschedule?: (meeting: Encounter) => void;
}

/* ---------- Status presentation ---------- */

const STATUS: Record<
  EncounterStatus,
  { label: string; color: ChipProps["color"] }
> = {
  requested: { label: "Requested", color: "warning" },
  active: { label: "Active", color: "success" },
  cancelled: { label: "Cancelled", color: "default" },
  completed: { label: "Completed", color: "default" },
};

const statusMeta = (s: EncounterStatus) =>
  STATUS[s] ?? { label: s.replace(/_/g, " "), color: "default" as const };

/* ---------- Component ---------- */

function MeetingCard({ meeting, onCancel, onReschedule }: MeetingCardProps) {
  const [copied, setCopied] = useState(false);

  console.log({ meeting });

  const start = parseISO(meeting.scheduled_at); // date-fns parses the offset → local Date
  const end = addMinutes(start, meeting.duration_minutes);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const isCancelled = meeting.status === "cancelled";
  const isOver = meeting.status === "completed" || isPast(end);
  const isActive = !isCancelled && !isOver;
  const canJoin = isActive && meeting.status === "active";

  const { label, color } = statusMeta(meeting.status);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(meeting.room_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable (insecure context) — fall back to selecting the link */
    }
  };

  return (
    <WhiteCard>
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        {/* Header: status + relative time */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Chip size="small" label={label} color={color} />
          {isActive && (
            <Typography variant="caption" color="text.secondary">
              {isPast(start)
                ? "In progress"
                : `in ${formatDistanceToNowStrict(start)}`}
            </Typography>
          )}
        </Stack>

        {/* Date block */}
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              minWidth: 56,
              textAlign: "center",
              borderRadius: 2,
              border: 1,
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: "block",
                bgcolor: isActive
                  ? "primary.main"
                  : "action.disabledBackground",
                color: isActive ? "primary.contrastText" : "text.secondary",
                fontWeight: 600,
                letterSpacing: 0.5,
                py: 0.25,
              }}
            >
              {format(start, "MMM").toUpperCase()}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
              {format(start, "d")}
            </Typography>
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                textDecoration: isCancelled ? "line-through" : "none",
              }}
            >
              {format(start, "EEEE, MMMM d")}
            </Typography>
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              color="text.secondary"
            >
              <ScheduleIcon sx={{ fontSize: 16 }} />
              <Typography
                variant="body2"
                sx={{ fontVariantNumeric: "tabular-nums" }}
              >
                {format(start, "H:mm")} – {format(end, "H:mm")}
                {" · "}
                {meeting.duration_minutes} min
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {tz}
            </Typography>
          </Box>
        </Stack>

        {/* Room link */}
        {!isCancelled && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            mt={2}
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: 2,
              bgcolor: "action.hover",
            }}
          >
            <VideocamIcon
              fontSize="small"
              color={canJoin ? "primary" : "disabled"}
            />
            <Link
              href={meeting.room_url}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              variant="body2"
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {meeting.room_url.replace(/^https?:\/\//, "")}
            </Link>
            <Tooltip title={copied ? "Copied" : "Copy link"}>
              <IconButton
                size="small"
                onClick={copyLink}
                aria-label="Copy meeting link"
              >
                {copied ? (
                  <CheckIcon fontSize="small" />
                ) : (
                  <ContentCopyIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        )}

        {/* Actions */}
        {(canJoin || (isActive && (onReschedule || onCancel))) && (
          <Stack direction="row" spacing={1} mt={2}>
            {canJoin && (
              <Button
                variant="contained"
                disableElevation
                fullWidth
                href={meeting.room_url}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<VideocamIcon />}
              >
                Join
              </Button>
            )}
            {isActive && onReschedule && (
              <Button
                variant="outlined"
                fullWidth
                onClick={() => onReschedule(meeting)}
              >
                Reschedule
              </Button>
            )}
            {isActive && onCancel && (
              <Button
                variant="text"
                color="inherit"
                onClick={() => onCancel(meeting)}
              >
                Cancel
              </Button>
            )}
          </Stack>
        )}

        {meeting.status === "requested" && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mt={1.5}
          >
            The join button becomes available once the meeting is confirmed.
          </Typography>
        )}
      </CardContent>
    </WhiteCard>
  );
}

export default MeetingCard;
